import type { KeyboardEvent } from "react"
import { LISTING_STATUSES, type RunStatusCounts } from "@nova-ai/shared"
import type { RunsSearch } from "../../lib/runs-list"
import { RUN_STATUS_LABELS } from "./run-status-label"

const tabLabels = { ...RUN_STATUS_LABELS, imported_with_errors: "With errors", all: "All" }
const tabs = [...LISTING_STATUSES, "all"] as const
export function RunsStatusTabs({
  status,
  counts,
  onChange,
}: {
  status: RunsSearch["status"]
  counts: RunStatusCounts | undefined
  onChange: (status: RunsSearch["status"]) => void
}) {
  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    let next: number
    switch (event.key) {
      case "ArrowRight":
        next = (index + 1) % tabs.length
        break
      case "ArrowLeft":
        next = (index + tabs.length - 1) % tabs.length
        break
      case "Home":
        next = 0
        break
      case "End":
        next = tabs.length - 1
        break
      default:
        return
    }
    event.preventDefault()
    const value = tabs[next]
    if (!value) {
      return
    }
    onChange(value)
    const buttons = event.currentTarget.parentElement?.querySelectorAll("button")
    buttons?.[next]?.focus()
  }
  return (
    <div className="relative -mx-4 sm:mx-0">
      <div
        data-runs-tabs
        className="flex gap-5 overflow-x-auto border-b border-border px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [mask-image:linear-gradient(to_right,#000_85%,transparent)] sm:px-0 sm:[mask-image:none]"
        role="tablist"
        aria-label="Run status"
      >
        {tabs.map((value, index) => (
          <button
            type="button"
            key={value}
            className="-mb-px inline-flex cursor-pointer items-center gap-1.5 border-b-2 border-transparent px-0.5 pt-2.5 pb-3 text-[14px] whitespace-nowrap text-muted-foreground transition-colors duration-150 ease-[ease] hover:text-foreground aria-selected:border-foreground aria-selected:font-medium aria-selected:text-foreground motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2"
            role="tab"
            id={`runs-tab-${value}`}
            aria-controls="runs-results"
            aria-selected={status === value}
            tabIndex={status === value ? 0 : -1}
            onClick={() => onChange(value)}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            {tabLabels[value]}

            <span className="text-[12px] text-muted-foreground tabular-nums">
              {tabCount(counts, value)}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

function tabCount(counts: RunStatusCounts | undefined, status: RunsSearch["status"]) {
  if (!counts) {
    return "—"
  }
  if (status === "all") {
    return Object.values(counts).reduce((total, count) => total + count, 0)
  }
  return counts[status]
}
